/**
 * @fileoverview Deals index route - displays the main deals list
 * @module routes/deals/index
 */

import type { ReactElement } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import {
  DealsHeader,
  DealsTabs,
  DealsContent,
  useDealsState
} from '@/features/deals';
import { BackgroundShape } from '@/components/ui/BackgroundShape';

/**
 * Deals index page component.
 *
 * Main deals page that displays fundraising opportunities for Web3 projects.
 * This component is rendered when visiting the base /deals route.
 *
 * Implements SOLID principles with single responsibility for page composition
 * and dependency inversion through hook abstraction.
 *
 * Features:
 * - Background shape for visual depth
 * - Header with title and subtitle
 * - Tab navigation between verified and closed deals
 * - Dynamic content based on active tab
 *
 * @component
 */
function DealsIndexPage(): ReactElement {
  const { activeTab, setActiveTab } = useDealsState();

  return (
    <div className="relative min-h-screen bg-background flex flex-col">
      {/* Background Shape */}
      <BackgroundShape />

      {/* Main Content */}
      <div className="relative z-10 w-full flex flex-col flex-1">
        {/* Header Section - Mobile Responsive */}
        <DealsHeader className={cn(
          'mb-10 md:mb-[60px]'  // Responsive margin
        )} />

        {/* Tabs Section with semantic border positioning */}
        <div className={cn(
          'max-w-[1300px] relative',
          'mx-4 md:mx-auto'  // Match landing page margins
        )}>
          {/* Top border that intersects exactly with VerticalBorders - Desktop Only */}
          <div
            className="hidden md:block absolute top-0 h-px bg-onicorn-gray-500 z-40"
            style={{
              left: 'calc(50% - 650px)',
              right: 'calc(50% - 650px)'
            }}
          />

          {/* Left vertical border - stops at tabs bottom - Desktop Only */}
          <div
            className="hidden md:block absolute w-px bg-onicorn-gray-500 z-40"
            style={{
              top: 0,
              bottom: 0,
              left: 'calc(50% - 221.75px)'
            }}
          />

          {/* Right vertical border - stops at tabs bottom - Desktop Only */}
          <div
            className="hidden md:block absolute w-px bg-onicorn-gray-500 z-40"
            style={{
              top: 0,
              bottom: 0,
              right: 'calc(50% - 221.75px)'
            }}
          />

          {/* Additional left vertical border - extended upward, stops at tabs bottom - Desktop Only */}
          <div
            className="hidden md:block absolute w-px bg-onicorn-gray-500 z-40"
            style={{
              top: 'calc(-12rem)',
              bottom: 0,
              left: 'calc(50% - 221.75px - 110px)' // 110px left of left border
            }}
          />

          {/* Additional right vertical border - extended upward, stops at tabs bottom - Desktop Only */}
          <div
            className="hidden md:block absolute w-px bg-onicorn-gray-500 z-40"
            style={{
              top: 'calc(-12rem)',
              bottom: 0,
              right: 'calc(50% - 221.75px - 110px)' // 110px right of right border
            }}
          />

          {/* Bottom border that intersects with vertical borders - Desktop Only */}
          <div
            className="hidden md:block absolute bottom-0 h-px bg-onicorn-gray-500 z-40"
            style={{
              left: 'calc(50% - 650px)',
              right: 'calc(50% - 650px)'
            }}
          />

          <DealsTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="mb-5"
          />
        </div>

        {/* Content Section */}
        <DealsContent activeTab={activeTab} />
      </div>
    </div>
  );
}

/**
 * Deals index route configuration.
 *
 * Defines the index route for /deals that displays the deals list.
 * This route is rendered inside the parent DealsLayout component.
 */
export const Route = createFileRoute('/deals/')({
  component: DealsIndexPage,
});
